/**
 * MUI Position Display component
 * 
 * This component shows device position information when hovering over devices
 */
import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { getIsMeter, getScale, getDrawingBox } from 'reduxModules/ducks/ui';
import { distanceToString } from 'utilities/format';
import { RootState } from 'types/redux/state';

interface PositionDisplayProps {
  mousePosition: { x: number; y: number };
  deviceInfo?: {
    type: string;
    position: { x: number; y: number; z?: number };
    id: string;
    model?: string;
    height?: number;
    horAngle?: number;
    verAngle?: number;
    mountingType?: string;
  };
  visible: boolean;
}

/**
 * MUI Position Display component
 * 
 * @param props - Component props
 * @returns {JSX.Element} Position display component
 */
const MuiPositionDisplay: React.FC<PositionDisplayProps> = ({ mousePosition, deviceInfo, visible }) => {
  const scale = useSelector(getScale);
  const isMeter = useSelector(getIsMeter);
  const { bbox } = useSelector(getDrawingBox);
  
  // Get the current active tab and area info
  const activeTab = useSelector((state: RootState) => state.container.activeTab);
  const areas = useSelector((state: RootState) => state.container.areas);

  if (!visible || !deviceInfo) return null;

  // Convert device position to room coordinates
  const roomX = distanceToString(deviceInfo.position.x, isMeter);
  const roomY = distanceToString(deviceInfo.position.y, isMeter);
  const roomZ = deviceInfo.position.z !== undefined ? distanceToString(deviceInfo.position.z, isMeter) : null;

  // Extract device number from ID and convert to 1-based
  const idParts = deviceInfo.id.split('-');
  const deviceIndex = parseInt(idParts[idParts.length - 1] || '0');
  const deviceNumber = deviceIndex + 1;
  
  // Format device type (remove 's' from plural and capitalize)
  const deviceTypeName = deviceInfo.type.endsWith('s') ? deviceInfo.type.slice(0, -1) : deviceInfo.type;
  const capitalizedDeviceType = deviceTypeName.charAt(0).toUpperCase() + deviceTypeName.slice(1);
  
  // Extract area ID from device ID and get the actual area name
  const areaId = idParts[0] || '';
  const currentArea = areas[activeTab] || areas[areaId];
  const roomName = currentArea?.name || (areaId.startsWith('_') ? areaId.substring(1) : areaId) || 'Unknown Room';
  
  // Function to wrap text if it's too long
  const wrapText = (text: string, maxLength = 25): string[] => {
    if (text.length <= maxLength) return [text];
    
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    
    for (const word of words) {
      if ((currentLine + ' ' + word).trim().length <= maxLength) {
        currentLine = currentLine ? currentLine + ' ' + word : word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);
    
    // If a single word is too long, truncate it
    return lines.map(line => line.length > maxLength ? line.substring(0, maxLength - 3) + '...' : line);
  };
  
  // Wrap the first line if needed
  const firstLineText = `${roomName} - ${capitalizedDeviceType} ${deviceNumber}`;
  const wrappedFirstLine = wrapText(firstLineText, 30);

  // Check if this is a wall speaker, bar microphone, or camera to show additional info
  const isWallSpeaker = deviceInfo.type === 'speakers' && deviceInfo.mountingType === 'wall';
  const isBarMicrophone = deviceInfo.type === 'microphones' && (deviceInfo.model?.includes('CBC 2500') || deviceInfo.model?.includes('VBC') || deviceInfo.model?.includes('ABC'));
  const isCamera = deviceInfo.type === 'cameras';
  const hasAngles = deviceInfo.horAngle !== undefined && deviceInfo.verAngle !== undefined;
  const hasHorAngle = deviceInfo.horAngle !== undefined;

  // Calculate number of lines
  let lineCount = wrappedFirstLine.length + 2; // Header + Model + Position
  if (deviceInfo.height !== undefined) lineCount++; // Height for any device type
  if (isWallSpeaker && hasAngles) lineCount += 2; // Azimuth and Elevation for wall speakers
  if (isBarMicrophone && hasHorAngle) lineCount++; // Horizontal angle for bar microphones
  if (isCamera && hasHorAngle) lineCount++; // Horizontal angle for cameras

  // Calculate tooltip height based on number of lines
  const tooltipHeight = lineCount * 20 + 10;

  // Track the current Y position for each line
  let currentY = 20;

  return (
    <PositionTooltip
      transform={`translate(${mousePosition.x + 10}, ${mousePosition.y - 10})`}
    >
      <PositionBackground
        width="220"
        height={tooltipHeight}
        rx="4"
        ry="4"
      />
      {wrappedFirstLine.map((line, index) => {
        const y = currentY;
        currentY += 15;
        return (
          <PositionText key={index} x="10" y={y}>
            {line}
          </PositionText>
        );
      })}
      <PositionText x="10" y={currentY}>
        Model: {deviceInfo.model || 'Unknown Model'}
      </PositionText>
      <PositionText x="10" y={currentY + 20}>
        X: {roomX}, Y: {roomY}{roomZ ? `, Z: ${roomZ}` : ''}
      </PositionText>
      {deviceInfo.height !== undefined && (
        <PositionText x="10" y={currentY + 40}>
          Height: {distanceToString(deviceInfo.height, isMeter)}
        </PositionText>
      )}
      {isWallSpeaker && hasAngles && (
        <>
          <PositionText x="10" y={currentY + (deviceInfo.height !== undefined ? 60 : 40)}>
            Azimuth: {deviceInfo.horAngle}°
          </PositionText>
          <PositionText x="10" y={currentY + (deviceInfo.height !== undefined ? 80 : 60)}>
            Elevation: {deviceInfo.verAngle}°
          </PositionText>
        </>
      )}
      {isBarMicrophone && hasHorAngle && !isWallSpeaker && (
        <PositionText x="10" y={currentY + (deviceInfo.height !== undefined ? 60 : 40)}>
          Horizontal Angle: {deviceInfo.horAngle}°
        </PositionText>
      )}
      {isCamera && hasHorAngle && (
        <PositionText x="10" y={currentY + (deviceInfo.height !== undefined ? 60 : 40)}>
          Horizontal Angle: {deviceInfo.horAngle}°
        </PositionText>
      )}
    </PositionTooltip>
  );
};

// Styled components using MUI styled API
const PositionTooltip = styled('g')({
  pointerEvents: 'none',
  zIndex: 1000,
});

const PositionBackground = styled('rect')({
  fill: '#333',
  stroke: '#666',
  strokeWidth: 1,
  opacity: 0.9,
});

const PositionText = styled('text')({
  fill: '#fff',
  fontSize: '12px',
  fontFamily: 'Arial, sans-serif',
});

export default MuiPositionDisplay;
